import styles from "./Propietario.module.css";
import stepsStyles from "../steps.module.css";
import Modal from "../../../Modals/ModalOwner";
import { useState, useEffect } from "react";
import { FieldError, CedulaStatus, ForeignToggle } from "../../../FieldHelpers";
import { useCedulaLookup } from "../../../../services/useCedulaLookup.js";
import { formatPhone } from "../../../../services/formatPhone";

export default function Propietario({ formData, setFormData, errors = {} }) {
  const [emails, setEmails] = useState(formData.propietario?.emailExtras || []);

  function handleAddEmail(e) {
    e.preventDefault();
    const newEmail = { id: Date.now(), email: "" };
    setEmails((prev) => [...prev, newEmail]);
    setFormData((prev) => ({
      ...prev,
      propietario: {
        ...prev.propietario,
        emailExtras: [...(prev.propietario?.emailExtras || []), newEmail],
      },
    }));
  }

  function handleEmailChange(id, value) {
    const updated = emails.map((item) =>
      item.id === id ? { ...item, email: value } : item,
    );
    setEmails(updated);
    setFormData((prev) => ({
      ...prev,
      propietario: { ...prev.propietario, emailExtras: updated },
    }));
  }

  function handleDeleteEmail(id) {
    const updated = emails.filter((item) => item.id !== id);
    setEmails(updated);
    setFormData((prev) => ({
      ...prev,
      propietario: { ...prev.propietario, emailExtras: updated },
    }));
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      propietario: { ...prev.propietario, [name]: value },
    }));
  }

  function handleToggle(name) {
    setFormData((prev) => ({
      ...prev,
      propietario: {
        ...prev.propietario,
        [name]: !prev.propietario[name],
      },
    }));
  }

  return (
    <div className="step">
        <h3 className="p-0 mt-2 mb-0 title" style={{fontSize:"1.3rem"}}>Información del propietario</h3>
        <p className={`${stepsStyles["inactive"]} mb-3` } style={{fontSize:"1rem"}}>Inserte los siguientes detalles para continuar.</p>

      <div className={stepsStyles["form-wrapper"]}>
        <OwnerIdentity
          formData={formData}
          handleChange={handleChange}
          setFormData={setFormData}
          errors={errors}
        />

        <OwnerContact
          formData={formData}
          handleChange={handleChange}
          setFormData={setFormData}
          errors={errors}
        />

        <OwnerEmails>
          <EmailPrincipal
            formData={formData}
            handleChange={handleChange}
            handleAddEmail={handleAddEmail}
            errors={errors}
          />
          {emails.map((item, idx) => (
            <EmailExtra
              key={item.id}
              id={item.id}
              email={item.email}
              handleEmailChange={handleEmailChange}
              handleDeleteEmail={handleDeleteEmail}
              error={errors.emailExtras?.[idx]}
            />
          ))}
        </OwnerEmails>

        <CoOwnerSection formData={formData} setFormData={setFormData} />
      </div>
    </div>
  );
}

// ── Identidad con búsqueda de cédula ────────────────────────────────────────
function OwnerIdentity({ formData, handleChange, setFormData, errors }) {
  const { buscar, resultado, estado, reset } = useCedulaLookup();
  const [autoloaded, setAutoloaded] = useState(false);

  // Aplicar datos del TSE/local cuando llegan
  useEffect(() => {
    if (resultado && !autoloaded) {
      setFormData((prev) => ({
        ...prev,
        propietario: {
          ...prev.propietario,
          nombre: resultado.nombre || prev.propietario.nombre,
          apellidos: resultado.apellidos || prev.propietario.apellidos,
        },
      }));
      setAutoloaded(true);
    }
  }, [resultado]);

  function handleCedulaChange(e) {
    handleChange(e);
    setAutoloaded(false);
    reset();
  }

  function handleCedulaBlur(e) {
    buscar(e.target.value);
  }

  return (
    <>
      {/* Cédula */}
      <div className="form-group">
        <label className="form-label">
          Cédula <span style={{ color: "#f87171" }}>*</span>
        </label>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1, position: "relative" }}>
            <input
              name="cedula"
              type="text"
              className={`${stepsStyles["form-control"]} form-control`}
              style={{ borderColor: errors.cedula ? "#ef4444" : undefined }}
              placeholder="Ej: 123456789"
              value={formData.propietario.cedula}
              onChange={handleCedulaChange}
              onBlur={handleCedulaBlur}
            />
          </div>
          <button
            type="button"
            onClick={() => buscar(formData.propietario.cedula)}
            disabled={estado === "buscando"}
            title="Buscar datos en el TSE"
            style={{
              padding: "0 12px",
              background: "rgba(249,115,22,0.15)",
              border: "1px solid rgba(249,115,22,0.4)",
              borderRadius: 6,
              color: "#f97316",
              cursor: "pointer",
              whiteSpace: "nowrap",
              fontSize: "0.78rem",
            }}
          >
            {estado === "buscando" ? (
              <i className="fa-solid fa-circle-notch fa-spin" />
            ) : (
              <>
                <i
                  className="fa-solid fa-magnifying-glass"
                  style={{ marginRight: 4 }}
                />
                Buscar
              </>
            )}
          </button>
        </div>
        <CedulaStatus estado={estado} />
        <FieldError msg={errors.cedula} />
      </div>

      {/* Nombre y Apellidos */}
      <div className="form-group row g-3 g-md-4">
        <div className="col-12 col-md-6">
          <label className="form-label">
            Nombre <span style={{ color: "#f87171" }}>*</span>
          </label>
          <input
            name="nombre"
            type="text"
            className={`${stepsStyles["form-control"]} form-control`}
            style={{ borderColor: errors.nombre ? "#ef4444" : undefined }}
            placeholder="Digite el nombre"
            value={formData.propietario.nombre}
            onChange={handleChange}
          />
          <FieldError msg={errors.nombre} />
        </div>
        <div className="col-12 col-md-6">
          <label className="form-label">
            Apellidos <span style={{ color: "#f87171" }}>*</span>
          </label>
          <input
            name="apellidos"
            type="text"
            className={`${stepsStyles["form-control"]} form-control`}
            style={{ borderColor: errors.apellidos ? "#ef4444" : undefined }}
            placeholder="Digite los apellidos"
            value={formData.propietario.apellidos}
            onChange={handleChange}
          />
          <FieldError msg={errors.apellidos} />
        </div>
      </div>

      {/* Nombre Sociedad */}
      <div className="form-group mt-3">
        <label className="form-label">
          Nombre de Sociedad
          <span style={{fontSize:"0.75rem",color:"#94a3b8",marginLeft:8}}>
            (si es persona jurídica — dejar Nombre/Apellidos vacíos)
          </span>
        </label>
        <input
          name="nombre_sociedad"
          type="text"
          className={`${stepsStyles["form-control"]} form-control`}
          placeholder="Ej: Inversiones Sol S.A."
          value={formData.propietario.nombre_sociedad || ""}
          onChange={handleChange}
        />
      </div>
    </>
  );
}

// ── Teléfonos y Placa ─────────────────────────────────────────────────────────
function OwnerContact({
  formData,
  handleChange,
  setFormData,
  errors,
}) {
  const chg = (field) => (e) =>
    setFormData(prev => ({
      ...prev,
      propietario: { ...prev.propietario, [field]: e.target.value },
    }));

  return (
    <>
      <div className="form-group row g-3 g-md-4 mt-1">
        <div className="col-12 col-md-4">
          <label className="form-label">Teléfono Casa</label>
          <input
            type="text"
            className={`${stepsStyles["form-control"]} form-control`}
            placeholder="Ej: 2222-1234"
            value={formData.propietario.telefono_casa || ""}
            onChange={chg("telefono_casa")}
          />
        </div>
        <div className="col-12 col-md-4">
          <label className="form-label">Teléfono Celular</label>
          <input
            type="text"
            className={`${stepsStyles["form-control"]} form-control`}
            placeholder="Ej: 8888-1234"
            value={formData.propietario.telefono_cel || ""}
            onChange={chg("telefono_cel")}
          />
        </div>
        <div className="col-12 col-md-4">
          <label className="form-label">Teléfono Trabajo</label>
          <input
            type="text"
            className={`${stepsStyles["form-control"]} form-control`}
            placeholder="Ej: 2200-1234"
            value={formData.propietario.telefono_trabajo || ""}
            onChange={chg("telefono_trabajo")}
          />
        </div>
      </div>

      <div className="form-group row g-3 g-md-4 mt-1">
        <div className="col-12 col-md-6">
          <label className="form-label">Placa de vehículo</label>
          <input
            name="placaVehiculo"
            type="text"
            className={`${stepsStyles["form-control"]} form-control`}
            placeholder={formData.propietario.placaExtranjera ? "Placa extranjera" : "Ej: ABC-123"}
            value={formData.propietario.placaVehiculo}
            onChange={handleChange}
          />
          <ForeignToggle
            checked={formData.propietario.placaExtranjera}
            onChange={() =>
              setFormData(prev => ({
                ...prev,
                propietario: { ...prev.propietario, placaExtranjera: !prev.propietario.placaExtranjera },
              }))
            }
            label="Placa extranjera"
          />
        </div>
        <div className="col-12 col-md-6">
          <label className="form-label">Notas</label>
          <textarea
            rows={2}
            className={`${stepsStyles["form-control"]} form-control`}
            placeholder="Observaciones opcionales..."
            value={formData.propietario.notas || ""}
            onChange={chg("notas")}
            style={{resize:"none"}}
          />
        </div>
      </div>
    </>
  );
}

// ── Email principal ──────────────────────────────────────────────────────────
function OwnerEmails({ children }) {
  return (
    <div
      className={`form-group`}
      style={{ display: "flex", flexDirection: "column", gap: 6 }}
    >
      <label className="form-label m-0">
        Email(s) <span style={{ color: "#f87171" }}>*</span>
      </label>
      {children}
    </div>
  );
}

function EmailPrincipal({ formData, handleChange, handleAddEmail, errors }) {
  return (
    <div>
      <div className="input-group">
        <input
          name="emailPrincipal"
          type="email"
          className={`${stepsStyles["form-control"]} form-control`}
          style={{ borderColor: errors.emailPrincipal ? "#ef4444" : undefined }}
          placeholder="email@ejemplo.com"
          value={formData.propietario.emailPrincipal}
          onChange={handleChange}
        />
        <button
          className={`btn`}
          style={{
            background: "rgba(249,115,22,0.15)",
            border: "1px solid rgba(249,115,22,0.4)",
            color: "#f97316",
          }}
          onClick={handleAddEmail}
          title="Agregar email adicional"
        >
          <i className="fa-solid fa-plus" />
        </button>
      </div>
      <FieldError msg={errors.emailPrincipal} />
    </div>
  );
}

function EmailExtra({
  id,
  email,
  handleEmailChange,
  handleDeleteEmail,
  error,
}) {
  return (
    <div>
      <div className="input-group">
        <input
          type="email"
          className={`${stepsStyles["form-control"]} form-control`}
          style={{ borderColor: error ? "#ef4444" : undefined }}
          placeholder="email@ejemplo.com"
          value={email}
          onChange={(e) => handleEmailChange(id, e.target.value)}
        />
        <button
          className="btn"
          style={{
            background: "rgba(248,113,113,0.1)",
            border: "1px solid rgba(248,113,113,0.3)",
            color: "#f87171",
          }}
          onClick={() => handleDeleteEmail(id)}
        >
          <i className="fa-solid fa-trash" />
        </button>
      </div>
      <FieldError msg={error} />
    </div>
  );
}

// ── Copropietarios ───────────────────────────────────────────────────────────
function CoOwnerSection({ formData, setFormData }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedIndex, setSelectedIndex] = useState(null);

  function openAdd(e) {
    e.preventDefault();
    setMode("add");
    setSelectedIndex(null);
    setOpen(true);
  }
  function openEdit(index) {
    setMode("edit");
    setSelectedIndex(index);
    setOpen(true);
  }
  function handleDelete(id) {
    setFormData((prev) => ({
      ...prev,
      copropietarios: prev.copropietarios.filter((o) => o.id !== id),
    }));
  }

  return (
    <>
      <div className={`form-group ${styles["form-group-coop"]}`}>
        <div>
          <h3 className="p-0 mt-2 mb-0 title" style={{fontSize:"1.1rem"}}>Copropietario</h3>
          <p className={`${stepsStyles["inactive"]}` } style={{fontSize:"0.9rem"}}>
            Agrega copropietarios si es necesario{" "}
            <span style={{ color: "var(--text-secondary)", fontSize: "0.78rem" }}>
              (opcional)
            </span>
          </p>
        </div>
        <button className={`${styles.btn}`} onClick={openAdd}>
          Agregar Copropietario
        </button>
      </div>

      <div className="listCoOwner d-flex flex-column" style={{ gap: "6px" }}>
        {formData.copropietarios.map((co) => (
          <CoOwner
            key={co.id}
            coOwner={co}
            onDelete={handleDelete}
            openEdit={openEdit}
          />
        ))}
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        formData={formData}
        setFormData={setFormData}
        mode={mode}
        selectedIndex={selectedIndex}
      />
    </>
  );
}

function CoOwner({ coOwner, onDelete, openEdit }) {
  // Para sociedades: nombre_sociedad; para personas: nombre + apellidos
  const nombreMostrado = coOwner.nombre_sociedad
    || `${coOwner.nombre || ""} ${coOwner.apellidos || ""}`.trim()
    || coOwner.cedula
    || "—";

  // Teléfono: preferir cel → casa → trabajo
  const tel = coOwner.telefono_cel || coOwner.telefono_casa || coOwner.telefono_trabajo || coOwner.telefono;

  return (
    <div className={styles.infoCoOwner}>
      <div className={styles.iconCoOwner}>
        <i className={`fa-solid ${coOwner.nombre_sociedad ? "fa-building" : "fa-user"}`} />
      </div>
      <div className={styles.identityCoOwner}>
        <h2 style={{ fontSize: "20px", margin: 0 }}>
          {nombreMostrado}
          {coOwner.cedula && nombreMostrado !== coOwner.cedula && (
            <span
              style={{ color: "var(--text-secondary)", fontWeight: 400, fontSize: "14px" }}
            >
              {" "}
              ({coOwner.cedula})
            </span>
          )}
        </h2>
        <p style={{ fontSize: "14px", margin: 0, color: "var(--text-muted)" }}>
          {tel && `📞 ${tel}`}
          {tel && coOwner.placaVehiculo && " · "}
          {coOwner.placaVehiculo && `🚗 ${coOwner.placaVehiculo}`}
        </p>
      </div>
      <div className={styles.actionsCoOwner}>
        <button
          className={styles["icon-btn"]}
          type="button"
          onClick={() => openEdit(coOwner.id)}
        >
          <i className="fa-solid fa-square-pen" />
        </button>
        <button
          className={styles["icon-btn"]}
          type="button"
          onClick={() => onDelete(coOwner.id)}
        >
          <i className="fa-solid fa-trash" />
        </button>
      </div>
    </div>
  );
}
